import React, { useEffect, useState } from "react";
import { toArray } from "lodash-es";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { copy } from "shared/assets";
import { VENUE_PATHS } from "Venues/router";
import {
  Table,
  TableColumn,
  TableSkeleton,
  FancyLink,
  Pager,
} from "shared/components";
import { Spacer } from "shared/layout";
import { IAPIState } from "shared/types";
import {
  buildUrl,
  formatAddress,
  formatDateTime,
  mapUserPermissions,
  isBandsDomain,
} from "shared/utils";
import {
  dispatchGetUserVenues,
  userVenueSelector,
  dataMetaSelector,
} from "Venues/state";
import { VENUE_ACTIONS, IVenue } from "Venues/types";
import { getDashboardVenueActions, usePagination } from "Venues/utils";
import {
  DeleteVenueModal,
  EditorsModal,
  RequestPublishModal,
  UnpublishModal,
  VenueBandsModal,
} from "Venues/containers";
import { Icon } from "shared/styles";
import { Checkmark, Stop } from "shared/assets/icons";
import { colors } from "shared/styles/constants";

const { venueProperties, dashboard } = copy;

export const UserVenueTable: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const userVenues = useSelector(userVenueSelector);
  const pagination = usePagination(VENUE_ACTIONS.getUserVenues);

  const { api: apiMeta } = useSelector(
    dataMetaSelector(VENUE_ACTIONS.getUserVenues)
  );
  const [deleteVenue, setDeleteVenue] = useState<{
    showModal: boolean;
    venueId?: string;
  }>({ showModal: false });
  const [editorsModal, setEditorsModal] = useState<{
    showModal: boolean;
    venueId?: string;
  }>({ showModal: false });
  const [publishModal, setPublishModal] = useState<{
    showModal: boolean;
    venueId?: string;
  }>({ showModal: false });
  const [unpublishModal, setUnublishModal] = useState<{
    showModal: boolean;
    venueId?: string;
  }>({ showModal: false });

  const [showBandsModal, setShowBandsModal] = useState<IVenue | null>(null);

  const getRowActions = (venueId: string) => {
    const venuePermissions =
      userVenues.find(({ id }) => id === venueId)?.permissions || [];

    return getDashboardVenueActions({
      venueId,
      history,
      userPermissions: mapUserPermissions(venuePermissions),
      openDeleteVenueModal: () => setDeleteVenue({ showModal: true, venueId }),
      openEditorsModal: () => setEditorsModal({ showModal: true, venueId }),
      openBandsModal: isBandsDomain
        ? () => setShowBandsModal(userVenues.find(({ id }) => id === venueId)!)
        : undefined,
      openRequestPublishModal: () =>
        setPublishModal({ showModal: true, venueId }),
      openUnpublishModal: () => setUnublishModal({ showModal: true, venueId }),
    });
  };

  useEffect(() => {
    dispatchGetUserVenues(dispatch);
  }, [dispatch]);

  const refetchUserVenues = (page: number) => {
    // scroll to top of page
    window.scrollTo({ top: 0, behavior: "smooth" });

    dispatchGetUserVenues(dispatch, page);
  };

  // if the last venue on a page is deleted (not on the first page) refetch the previous page
  const onDelete = () => {
    if (userVenues.length <= 1 && pagination.currentPage !== 1) {
      refetchUserVenues(pagination.currentPage - 1);
    }
  };

  if (apiMeta?.status === IAPIState.pending) {
    return <TableSkeleton />;
  }

  return (
    <>
      {deleteVenue.showModal && deleteVenue.venueId && (
        <DeleteVenueModal
          venueId={deleteVenue.venueId}
          handleClose={() => setDeleteVenue({ showModal: false })}
          onDelete={onDelete}
        />
      )}
      {editorsModal.showModal && editorsModal.venueId && (
        <EditorsModal
          venueId={editorsModal.venueId}
          handleClose={() => setEditorsModal({ showModal: false })}
          refetchUserVenues={() => refetchUserVenues(pagination.currentPage)}
        />
      )}
      {publishModal.showModal && publishModal.venueId && (
        <RequestPublishModal
          venueId={publishModal.venueId}
          handleClose={() => setPublishModal({ showModal: false })}
        />
      )}
      {unpublishModal.showModal && unpublishModal.venueId && (
        <UnpublishModal
          venueId={unpublishModal.venueId}
          handleClose={() => setUnublishModal({ showModal: false })}
        />
      )}
      {showBandsModal && (
        <VenueBandsModal
          venue={showBandsModal}
          handleClose={() => setShowBandsModal(null)}
        />
      )}
      <Spacer size="s">
        <Table
          columns={columns}
          data={toArray(userVenues)}
          getRowActions={getRowActions}
          emptyCopy={dashboard.emptyVenueList}
        />
        <Pager {...pagination} onPageChange={refetchUserVenues} />
      </Spacer>
    </>
  );
};

const columns: TableColumn[] = [
  {
    Header: venueProperties.name.title,
    accessor: "title",
    Cell: ({ value, row }) => {
      const venueId = (row.original as IVenue)?.id;
      return (
        <FancyLink to={buildUrl(VENUE_PATHS.VENUE, { venueId })}>
          {value}
        </FancyLink>
      );
    },
  },
  {
    Header: venueProperties.updated.title,
    accessor: "updatedAt",
    Cell: ({ value }) => formatDateTime(value),
    hideOnMobile: true,
  },
  {
    Header: venueProperties.status.title,
    accessor: "published",
    Cell: ({ value }) => (
      <Spacer
        spaceDirection="horizontal"
        size="xxs"
        flexWrap="nowrap"
        align="center"
      >
        <Icon
          as={value ? Checkmark : Stop}
          color={value ? colors.green : colors.red}
        />
        <span>
          {value
            ? venueProperties.status.values.published
            : venueProperties.status.values.unpublished}
        </span>
      </Spacer>
    ),
  },
  {
    Header: venueProperties.address.title,
    accessor: "address",
    Cell: ({ value }) => {
      if (value) {
        const { address, country } = formatAddress(value);
        return `${address}, ${country?.name}`;
      }
      return "";
    },
    hideOnMobile: true,
  },
];
